<template>
  <div
    :class="[
      'img-wrap',
      isAcc ? 'acc-img-wrap' : '',
      hasInfoBgColor && 'has-info-bg-color',
      hasHoverImage && 'has-hover-image',
      cardType === CARD_TYPE.CARD && 'isCardType',
      cardType === CARD_TYPE.GOODS_LIST && 'isGoodsListType',
    ]"
    :style="imageRatio ? { 'aspect-ratio': imageRatio } : {}"
  >
    <div :class="['img-wrap-inner', isNotAvailable && 'sold-out-img-inner']">
      <NuxtImgWrap
        :src="akamai(mainImage.src)"
        class="good-img"
        :class="{ 'product-gray': mainImage.isGray }"
        :alt="imgAlt ? `${imgAlt} ${productTitle}` : productTitle"
        :loading="imageLoadMode"
      />
      <NuxtImgWrap
        v-if="hasHoverImage"
        :src="akamai(hoverImage.src)"
        class="good-img2"
        :class="{ 'product-gray': hoverImage.isGray }"
        :alt="imgAlt ? `${imgAlt} ${productTitle}` : productTitle"
        :loading="imageLoadMode"
      />
    </div>
    <div v-if="product.status == 2" class="sold-out-block">Not Available</div>
    <div v-else-if="product.stock !== undefined && product.stock <= 0" class="sold-out-block">
      {{ soldOutText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductsListItem } from '@/api/types/product';
import { akamai } from '@/assets/js/utils';
import { useProductImage } from '@/composables/useProductImage';
import type { CardTypeValue } from '../constants';
import { CARD_TYPE } from '../constants';
import type { ImageLoadMode } from '../types';

interface Props {
  product: ProductsListItem;
  hasInfoBgColor?: boolean;
  showMaterialImg?: boolean;
  cardType?: CardTypeValue;
  imageRatio?: string;
  imageLoadMode?: ImageLoadMode;
  soldOutText?: string;
  imgAlt?: string;
}

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}) as ProductsListItem,
  hasInfoBgColor: false,
  showMaterialImg: false,
  cardType: CARD_TYPE.GOODS_LIST,
  imageRatio: '',
  imageLoadMode: 'lazy',
  soldOutText: 'Sold Out',
  imgAlt: '',
});

const { mainImage, hoverImage } = useProductImage(props);

const isAcc = computed<boolean>(() => {
  return props?.product?.is_glass !== 1;
});

const hasHoverImage = computed<boolean>(() => {
  return !!hoverImage.value?.src;
});

const isNotAvailable = computed<boolean>(() => {
  return (
    props?.product?.status == 2 ||
    (props?.product?.stock !== undefined && props?.product?.stock <= 0)
  );
});

const productTitle = computed<string>(() => {
  return props.product?.title || props.product?.name || '';
});
</script>

<style scoped lang="scss">
.img-wrap {
  @include flex(center, center, column);

  position: relative;
  width: 100%;
  background-color: #f2f2f2;
  aspect-ratio: 1/1;
  &.has-hover-image {
    &:hover {
      img {
        &.good-img {
          opacity: 0;
        }
        &.good-img2 {
          opacity: 1;
        }
      }
      .sold-out-img-inner {
        img {
          &.good-img2 {
            opacity: 0.4;
          }
        }
      }
    }
  }
  .img-wrap-inner {
    @include flex(center, center, column);
  }
  .sold-out-img-inner {
    img {
      opacity: 0.4;
    }
  }
  img {
    display: block;
    margin: auto;
    width: 80%;
    aspect-ratio: 2/1;
    object-fit: cover;
    mix-blend-mode: multiply;
    cursor: pointer;
    &.good-img {
      position: relative;
      z-index: 2;
    }
    &.good-img2 {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      opacity: 0;
      aspect-ratio: 2/1;
    }
    &.product-gray {
      mix-blend-mode: normal;
    }
  }
  &.acc-img-wrap {
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      mix-blend-mode: normal;
      aspect-ratio: 1/1;
      &.good-img2 {
        width: 100%;
        height: 100%;
        object-fit: contain;
        aspect-ratio: 1/1;
      }
    }
  }
  &.has-info-bg-color {
    justify-content: flex-end;
    aspect-ratio: 2/1;
    img {
      &.good-img {
        margin: 0;
      }
      &.good-img2 {
        top: auto;
        bottom: 0;
        transform: translate(-50%, 0);
      }
    }
  }
  &.isCardType {
    aspect-ratio: 432/358;
    box-sizing: border-box;
    width: inherit;
    img {
      object-fit: cover;
      &.good-img {
        margin: 0;
      }
      &.good-img2 {
        top: 50%;
        bottom: auto;
        transform: translate(-50%, -50%);
        object-fit: cover;
      }
    }
    &.acc-img-wrap {
      padding-top: 0;
      img {
        &.good-img2 {
          padding-top: 0;
        }
      }
    }
  }
  &.isGoodsListType {
    &.acc-img-wrap {
      img {
        object-fit: cover;
        &.good-img2 {
          object-fit: cover;
        }
      }
    }
  }
}
.sold-out-block {
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  text-align: center;
  font-family: $f-pb;
  color: #b30101;
  cursor: pointer;

  @include flex(center, center);
  @include adaptionSize(16);
}
</style>
